import { isLoggedInAtom, isOnboardingAtom, userInfoAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useDeviceHeight } from 'app/hooks/use-device-size';
import { useForbidBackAction } from 'app/hooks/use-forbid-back-action';
import { useUser, useUserTypesConfig } from 'app/network/user';
import { useNavigateToSignUpForm } from 'app/screens/onboarding/signup';
import { useTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import { UserType } from 'app/types/user';
import { usePageTitle } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { LoginSheet, useLoginSheet } from './login';
import { ChooseUserTypeButton } from './choose-user-type-button';
const ChooseUserTypeScreenComponent: NavigationScreen<'chooseUserType'> = ({ navigation }) => {
  const theme = useTheme();
  const { t } = useScopedTranslation();

  const setPageTitle = usePageTitle('chooseUserType');

  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const { data: userTypes } = useUserTypesConfig();

  const { loginSheetState } = useLoginSheet();
  const [, setIsLoginSheetVisible] = loginSheetState;

  const pagePadding = usePagePadding();

  const isOnboarding = useAtomValue(isOnboardingAtom);

  useForbidBackAction(isOnboarding);

  const onPress = (userType: string) => {
    navigation.navigate(
      'onboarding',
      {
        screen: 'contact-information',
        params: { userType },
      },
      { pop: true },
    );
  };

  const deviceHeight = useDeviceHeight();

  const onLoginPress = () => {
    setIsLoginSheetVisible(true);
  };

  const navigateToSignUpForm = useNavigateToSignUpForm();

  const onLoginComplete = () => {
    navigateToSignUpForm();
  };

  const isLoggedIn = useAtomValue(isLoggedInAtom);

  useUser({
    enabled: isLoggedIn,
  });

  // On successful login, if the user finished the onboarding, navigate to the home screen
  const userInfo = useAtomValue(userInfoAtom);
  useEffect(() => {
    if (isLoggedIn && !!userInfo?.acceptedGuidelineDate) {
      navigation.navigate('main', { screen: 'billboard', params: { screen: 'all-listings' } }, { pop: true });
    }
  }, [isLoggedIn, navigation, userInfo]);

  return (
    <Page
      paddingStyle={{
        ...pagePadding,
        paddingTop: pagePadding.paddingTop + theme.spacing['spacing.general.x4'],
      }}
      style={{ backgroundColor: theme.colors['white-label.splash'] }}
    >
      <Row alignItems="center" style={{ height: deviceHeight - 2 * pagePadding.paddingTop }}>
        <Column gap="spacing.general.x6" width="100%">
          {!isLoggedIn ? (
            <Column gap="spacing.general.x6">
              <Text color="surface.default" fontSize={32} fontWeight="700">
                {t('title')}
              </Text>
              <Text color="surface.default" variant="headerXSmall">
                {t('alreadyHaveAccount')}
              </Text>
              <Button onPress={onLoginPress} variant="primary">
                <Text color="surface.default" variant="headerXSmall" fontWeight="700">
                  {t('clickHere')}
                </Text>
              </Button>
              <Text color="surface.default" variant="headerXSmall">
                {t('instructions')}
              </Text>
            </Column>
          ) : (
            <Column gap="spacing.general.x6">
              <Button
                onPress={() => {
                  return;
                }}
                variant="primary"
              >
                <Column alignItems="center">
                  <Text color="surface.default" variant="headerXSmall" fontWeight="700">
                    {t('loggedIn')}
                  </Text>
                  <Text color="surface.default" variant="bodyBase" fontWeight="500">
                    {t('loggedInRedirect')}
                  </Text>
                </Column>
              </Button>
              <Text color="surface.default" variant="headerXSmall">
                {t('instructionsLoggedIn')}
              </Text>
            </Column>
          )}

          <Column gap="spacing.general.x6" marginTop="spacing.general.x4">
            {userTypes?.map(({ name: userType }) => (
              <ChooseUserTypeButton
                backgroundColor={
                  userType === UserType.PRO
                    ? theme.colors['surface.tertiary']
                    : theme.colors['surface.brand.secondary-3.default']
                }
                foregroundColor={
                  userType === UserType.PRO
                    ? theme.colors['white-label.splash']
                    : theme.colors['text.secondary-3.strong']
                }
                key={userType}
                onPress={() => onPress(userType)}
                userType={userType}
              />
            ))}
          </Column>
        </Column>
      </Row>
      <LoginSheet onLoginComplete={onLoginComplete} state={loginSheetState} />
    </Page>
  );
};

export const ChooseUserTypeScreen: NavigationScreen<'chooseUserType'> =
  withErrorBoundary(ChooseUserTypeScreenComponent);
